import {
  lazy,
  Suspense,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { userService } from "../../../modules/user/services";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
} from "../../../modules/homeDisplay/hooks";
import {
  ActivityType,
  TimingType,
} from "../../../modules/homeDisplay/types/homeDisplay.types";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "../../../utils/navigation";
import {
  usePhoneVerifyActivityList,
  useIsHomeDisplayReady,
  OPEN_PHONE_VERIFY_ACTIVITY_EVENT,
} from "../../../modules/homeDisplay/hooks";
import { loadConfig } from "@/modules";
import type { PhoneVerifyActivityItem } from "../components/PhoneVerifyActivity/PhoneVerifyActivity.types";

const PhoneVerifyActivityDialog = lazy(() =>
  import("../components/PhoneVerifyActivity/PhoneVerifyActivityDialog").then(
    m => ({ default: m.PhoneVerifyActivityDialog })
  )
);
const PhoneVerifyActivityCongratsDialog = lazy(() =>
  import(
    "../components/PhoneVerifyActivity/PhoneVerifyActivityCongratsDialog"
  ).then(m => ({ default: m.PhoneVerifyActivityCongratsDialog }))
);

const PHONE_VERIFY_CONFIG = POPUP_CONFIGS.PHONE_VERIFY_ACTIVITY;

/**
 * 验证弹窗 → 成功弹窗 的流程容器
 * 验证成功后关闭验证弹窗、弹成功弹窗；Start Playing = 关闭 + 刷新余额 + 刷新首页配置（浮标消失）
 */
const PhoneVerifyActivityFlow: React.FC<{
  activity: PhoneVerifyActivityItem;
  onComplete: () => void;
}> = ({ activity, onComplete }) => {
  const { loadUserRealtime } = useUserDataFlattened();
  const [stage, setStage] = useState<"verify" | "congrats">("verify");
  const [grantedAmount, setGrantedAmount] = useState<number>(0);
  /** 本次验证改绑了登录手机号时为新号（带区号），否则 null（成功弹窗据此显示提示行） */
  const [changedLoginPhone, setChangedLoginPhone] = useState<string | null>(
    null
  );

  const handleSuccess = useCallback(
    (
      rewardAmount: number,
      info?: { phoneChanged: boolean; loginPhone: string }
    ) => {
      setGrantedAmount(rewardAmount);
      setChangedLoginPhone(info?.phoneChanged ? info.loginPhone : null);
      setStage("congrats");
    },
    []
  );

  const handleCongratsClose = useCallback(() => {
    if (changedLoginPhone) {
      // 改绑了登录手机号：强制登出并去登录页重新登录（按钮文案为 Login Now）
      onComplete();
      userService.logout(ROUTES.LOGIN);
      return;
    }
    // 未改绑：刷新余额（奖金已同步入账）+ 刷新首页配置（已验证用户后端不再下发，浮标消失）
    loadUserRealtime().catch(() => {});
    loadConfig();
    onComplete();
  }, [changedLoginPhone, loadUserRealtime, onComplete]);

  return (
    <Suspense fallback={null}>
      {stage === "verify" ? (
        <PhoneVerifyActivityDialog
          visible
          activity={activity}
          onClose={onComplete}
          onSuccess={handleSuccess}
        />
      ) : (
        <PhoneVerifyActivityCongratsDialog
          visible
          rewardAmount={grantedAmount}
          loginPhone={changedLoginPhone ?? undefined}
          onClose={handleCongratsClose}
        />
      )}
    </Suspense>
  );
};

export interface PhoneVerifyActivityPopupResult {
  /** 是否有可展示的活动（浮标可见性） */
  shouldShow: boolean;
  /**
   * 手动打开弹窗（本活动无自动弹窗时机，仅手动触发）。
   * 可传入口携带的 activityId：list 是该国全部启用配置（人群过滤在展示项/登记时才做），
   * 初始渲染按该 id 找对应配置展示金额/entryPoint；status 返回后一律以 status 快照为准。
   */
  trigger: (activityId?: string) => void;
  /** 指定活动是否在桶 list 中（浮标可见性判定；不传 id 则看 list 是否非空） */
  isActivityAvailable: (activityId?: string) => boolean;
}

/**
 * 新手机号验证活动弹窗 hook
 * - 仅手动触发（autoTrigger: false），点右下角浮标/入口打开
 * - shouldShow = 配置桶里有活动（已验证用户后端不下发，浮标自动消失）
 * - 弹窗打开时组件内部调 status（参与登记 + 预填手机号 + participated 防御）
 * - 多活动：trigger 携带入口 activityId（参考 rescueFund.trigger(taskId)），
 *   banner 点击经 OPEN_PHONE_VERIFY_ACTIVITY_EVENT 传入（参考 OPEN_RESCUE_FUND_EVENT）
 */
export const usePhoneVerifyActivityPopup =
  (): PhoneVerifyActivityPopupResult => {
    const { data: userInfo, isAuthenticated } = useUserDataFlattened();
    const isConfigReady = useIsHomeDisplayReady();
    const list = usePhoneVerifyActivityList();

    // 入口携带的 activityId（参考 rechargeBet 的 manualActivityRef：ref 同步写、renderContent 触发时读）
    const manualActivityIdRef = useRef<string | null>(null);

    // 「获得活动参与资格时弹出」时机接入（照 useSevenDayRechargePopup）：
    // priority 取 admin 弹窗排序配置，autoTrigger 按该时机下本活动的启用开关
    const priority = useActivityPopupPriority(
      ActivityType.PHONE_VERIFY_ACTIVITY,
      PHONE_VERIFY_CONFIG.priority,
      TimingType.ACTIVITY_QUALIFICATION
    );
    const isAutoEnabled = useIsActivityQualificationAutoEnabled(
      ActivityType.PHONE_VERIFY_ACTIVITY
    );

    const shouldShow = isConfigReady && isAuthenticated && list.length > 0;

    const { trigger: originalTrigger } = useQueuedPopup({
      popupId: PHONE_VERIFY_CONFIG.id,
      priority,
      timingType: TimingType.ACTIVITY_QUALIFICATION,
      shouldShow: () => isConfigReady && isAuthenticated && list.length > 0,
      renderContent: onComplete => {
        // 初始展示的配置：入口携带 id 优先，找不到回落 list[0]
        const manualId = manualActivityIdRef.current;
        const resolved =
          (manualId && list.find(item => item?.id === manualId)) || list[0];
        if (!resolved) return null;
        return (
          <PhoneVerifyActivityFlow
            activity={resolved}
            onComplete={() => {
              manualActivityIdRef.current = null;
              onComplete();
            }}
          />
        );
      },
      // 自动弹每人每天最多一次；手动点浮标不受限
      showOnce: true,
      showOnceGranularity: "day",
      allowManualTrigger: true,
      // 「获得活动参与资格时弹出」由 admin 弹窗排序里的启用开关控制
      autoTrigger: isAutoEnabled,
      allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.EVENT_CENTER],
      dependencies: [list, isAuthenticated, isConfigReady, isAutoEnabled],
      userBinding: true,
      getUserId: () => userInfo?.userId,
    });

    // 包装 trigger：支持入口携带 activityId（同 rescueFund.trigger(taskId) 语义）
    const trigger = useCallback(
      (activityId?: string) => {
        manualActivityIdRef.current = activityId || null;
        originalTrigger();
      },
      [originalTrigger]
    );

    // 监听首页 banner / useDisplayItemClick 派发的「打开手机验证弹窗」事件（同 OPEN_RESCUE_FUND_EVENT 模式）
    useEffect(() => {
      const handler = (e: Event) => {
        const activityId = (e as CustomEvent<{ activityId?: string }>).detail
          ?.activityId;
        trigger(activityId);
      };
      window.addEventListener(OPEN_PHONE_VERIFY_ACTIVITY_EVENT, handler);
      return () =>
        window.removeEventListener(OPEN_PHONE_VERIFY_ACTIVITY_EVENT, handler);
    }, [trigger]);

    // 浮标可见性判定：banner.activityId 在 list 中存在即显示（list 为该国全部启用配置）
    const isActivityAvailable = useCallback(
      (activityId?: string) => {
        if (!activityId) return list.length > 0;
        return list.some(item => item?.id === activityId);
      },
      [list]
    );

    return {
      shouldShow,
      trigger,
      isActivityAvailable,
    };
  };
